import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  type IncomingQuoteRequest,
  type QuoteLineInput,
  type SellerListingRow,
} from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from '@/features/admin/AdminError';

/**
 * Prescriptions waiting to be priced.
 *
 * ── A QUEUE, WORKED STANDING UP ─────────────────────────────────────────────
 *
 * A pharmacist does this between customers, on a phone, one-handed. So the row
 * leads with the typed summary — "Amoxil 500mg × 21 caps" is readable in a
 * second — and the photograph is one tap away for the cases that need it, which
 * is most prescription-only ones.
 *
 * ── OPENING THE PHOTOGRAPH IS AN EVENT ──────────────────────────────────────
 *
 * §9.15: every read is logged, and the BUYER is shown which pharmacies opened
 * theirs and when. The button says so, because a pharmacist should know that
 * before they tap it rather than afterwards.
 */
export function QuoteQueueScreen() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['seller', 'quote-requests'],
    queryFn: ({ signal }) => api.incomingQuoteRequests(signal),
    refetchInterval: 60_000,
  });

  const [pricing, setPricing] = useState<string | null>(null);

  if (isError) {
    return <AdminError title="We couldn't load your requests" error={error} onRetry={() => refetch()} />;
  }

  if (pricing) {
    return <QuoteBuilder reference={pricing} onDone={() => setPricing(null)} />;
  }

  const requests = data?.requests ?? [];

  return (
    <div className="mx-auto w-full max-w-[720px] px-4 py-6 xl:px-8">
      <header className="mb-5">
        <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Prescriptions</h1>
        <p className="mt-1 max-w-[62ch] text-[13px] leading-relaxed text-muted">
          Buyers sending you a prescription and asking what it costs. Price it from your own stock —
          if they accept, it becomes an ordinary order and you pack it the usual way.
        </p>
      </header>

      {isLoading ? (
        <div className="space-y-2.5">
          <Skeleton className="h-28 w-full" radius="card" />
          <Skeleton className="h-28 w-full" radius="card" />
        </div>
      ) : requests.length === 0 ? (
        <EmptyState
          title="Nothing waiting"
          body="When a buyer near you sends a prescription, it lands here. Replying quickly is the whole advantage — most accept the first good answer."
          action={<Button variant="grey" onClick={() => refetch()}>Check again</Button>}
        />
      ) : (
        <div className="space-y-2.5">
          {requests.map((request) => (
            <RequestRow key={request.reference} request={request} onPrice={() => setPricing(request.reference)} />
          ))}
        </div>
      )}
    </div>
  );
}

function RequestRow({
  request,
  onPrice,
}: {
  request: IncomingQuoteRequest;
  onPrice: () => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [declining, setDeclining] = useState(false);

  const decline = useMutation({
    mutationFn: () => api.declineQuoteRequest(request.reference, reason.trim() || undefined),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['seller', 'quote-requests'] }),
  });

  return (
    <Card className={cn('!p-4', request.is_veterinary && 'border-blue')}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
          {request.reference}
        </span>
        {request.is_veterinary ? (
          <span className="rounded-pill border border-blue px-2 py-[1px] font-mono text-[9.5px] uppercase tracking-[0.12em] text-blue">
            Animal
          </span>
        ) : null}
        {request.status === 'quoted' ? <Badge tone="verified">You replied</Badge> : null}
        {request.status === 'declined' ? <Badge tone="neutral">You declined</Badge> : null}
      </div>

      {/* The typed list first. It is what makes this answerable in a hurry. */}
      <p className="m-0 mt-1.5 text-[14px] font-bold text-ink">
        {request.summary || 'See the prescription'}
      </p>

      {request.is_veterinary && request.species_label ? (
        <p className="m-0 mt-0.5 text-[12.5px] text-blue">
          For {request.species_label.toLowerCase()}
          {request.animal_note ? ` · ${request.animal_note}` : ''}
        </p>
      ) : null}

      {request.my_quote ? (
        <p className="m-0 mt-1.5 text-[12.5px] text-muted">
          You quoted {request.my_quote.goods_total_display}.
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        {request.has_prescription ? <PrescriptionButton reference={request.reference} /> : null}

        {request.status !== 'declined' ? (
          <Button size="sm" onClick={onPrice}>
            {request.my_quote ? 'Change your price' : 'Price it'}
          </Button>
        ) : null}

        {request.status === 'pending' ? (
          <Button size="sm" variant="grey" onClick={() => setDeclining((open) => !open)}>
            Cannot fill it
          </Button>
        ) : null}
      </div>

      {declining ? (
        <div className="mt-3 border-t border-line pt-3">
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="We do not stock that strength"
            className="min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[13.5px] text-ink outline-none focus:border-blue"
          />
          <p className="m-0 mt-1.5 text-[11.5px] leading-relaxed text-muted">
            The buyer sees this. Saying why is more useful to them than silence, and it stops us
            chasing you about it.
          </p>
          <Button size="sm" className="mt-2" disabled={decline.isPending} onClick={() => decline.mutate()}>
            {decline.isPending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

/**
 * Opening the paper.
 *
 * Fetched as a blob rather than linked, because it needs the bearer token and
 * because there is deliberately no shareable URL for a prescription.
 */
function PrescriptionButton({ reference }: { reference: string }) {
  const [url, setUrl] = useState<string | null>(null);

  const open = useMutation({
    mutationFn: () => api.prescriptionBlob(reference),
    onSuccess: (blob) => setUrl(URL.createObjectURL(blob)),
  });

  useEffect(() => () => {
    if (url) URL.revokeObjectURL(url);
  }, [url]);

  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex min-h-11 items-center rounded-btn border border-line px-3.5 text-[13px] font-semibold text-ink no-underline"
      >
        Open the photo
      </a>
    );
  }

  return (
    <Button size="sm" variant="grey" disabled={open.isPending} onClick={() => open.mutate()}>
      {open.isPending ? 'Opening…' : 'See the prescription'}
    </Button>
  );
}

/**
 * Pricing it.
 *
 * Only their own listings, because a quote has to be a price they can honour —
 * and because ChiCare has no drug facts for a product nobody has listed (§9.1).
 * Anything missing from the list is a thing they need to add to their stock
 * first, which is a different screen and deliberately so.
 */
function QuoteBuilder({ reference, onDone }: { reference: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [lines, setLines] = useState<QuoteLineInput[]>([]);
  const [note, setNote] = useState('');
  const [search, setSearch] = useState('');

  const request = useQuery({
    queryKey: ['seller', 'quote-request', reference],
    queryFn: ({ signal }) => api.incomingQuoteRequest(reference, signal),
  });

  const listings = useQuery({
    queryKey: ['seller', 'listings'],
    queryFn: ({ signal }) => api.sellerListings(signal),
  });

  const send = useMutation({
    mutationFn: () => api.submitQuote(reference, { lines, note: note.trim() || undefined }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['seller', 'quote-requests'] });
      onDone();
    },
  });

  const rows: SellerListingRow[] = listings.data?.listings ?? [];
  const matches = search.trim()
    ? rows.filter((row) => (row.product.name ?? "").toLowerCase().includes(search.trim().toLowerCase()))
    : rows.slice(0, 8);

  const error = send.error instanceof ApiError ? send.error : null;

  const total = lines.reduce((sum, line) => {
    const row = rows.find((one) => one.id === line.listing_id);

    return sum + (row ? row.price_kobo * line.quantity : 0);
  }, 0);

  return (
    <div className="mx-auto w-full max-w-[720px] px-4 py-6 xl:px-8">
      <button
        type="button"
        onClick={onDone}
        className="-mx-1 mb-2 inline-flex min-h-11 items-center gap-1.5 rounded-btn px-1 text-[12.5px] font-medium text-blue"
      >
        <Icon name="back" size={14} strokeWidth={2.4} />
        Back to the queue
      </button>

      <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">Price this request</h1>

      {request.data ? (
        <Card className="mt-3 !p-4">
          <p className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
            What they asked for
          </p>
          <p className="m-0 mt-1 text-[13.5px] font-medium text-ink">
            {request.data.summary || '(nothing typed — open the prescription)'}
          </p>
          {request.data.note ? (
            <p className="m-0 mt-1.5 text-[12.5px] leading-relaxed text-muted">{request.data.note}</p>
          ) : null}
          {request.data.is_veterinary && request.data.species_label ? (
            <p className="m-0 mt-1.5 text-[12.5px] text-blue">
              For {request.data.species_label.toLowerCase()}
              {request.data.animal_note ? ` · ${request.data.animal_note}` : ''}
            </p>
          ) : null}
          {request.data.delivering_to ? (
            <p className="m-0 mt-1.5 text-[11.5px] text-faint">
              Delivering to {request.data.delivering_to}
            </p>
          ) : null}
          {request.data.has_prescription ? (
            <div className="mt-3">
              <PrescriptionButton reference={reference} />
            </div>
          ) : null}
        </Card>
      ) : null}

      <section className="mt-4">
        <h2 className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
          Add from your stock
        </h2>

        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search your listings"
          className="mt-2 min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
        />

        <div className="mt-2 overflow-hidden rounded-card border border-line bg-white">
          {matches.length === 0 ? (
            <p className="m-0 px-4 py-3 text-[13px] text-muted">
              Nothing of yours matches. Add it to your stock first and it will appear here.
            </p>
          ) : (
            matches.map((row) => (
              <div
                key={row.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line-2 px-4 py-2.5 last:border-b-0"
              >
                <span className="min-w-[160px] flex-1">
                  <span className="block text-[13.5px] font-medium text-ink">
                    {row.product.name ?? ""}
                  </span>
                  <span className="block font-mono text-[11.5px] text-faint">
                    {row.price_display}
                  </span>
                </span>

                <Button
                  size="sm"
                  variant="grey"
                  disabled={lines.some((line) => line.listing_id === row.id)}
                  onClick={() => setLines((current) => [...current, { listing_id: row.id, quantity: 1 }])}
                >
                  {lines.some((line) => line.listing_id === row.id) ? 'Added' : 'Add'}
                </Button>
              </div>
            ))
          )}
        </div>
      </section>

      {lines.length > 0 ? (
        <section className="mt-4">
          <h2 className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
            Your reply
          </h2>

          <div className="mt-2 space-y-2.5">
            {lines.map((line, index) => {
              const row = rows.find((one) => one.id === line.listing_id);

              return (
                <Card key={line.listing_id} className="!p-3.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="min-w-0 flex-1 text-[13.5px] font-medium text-ink">
                      {row?.product.name}
                    </span>
                    <input
                      type="number"
                      min={1}
                      value={line.quantity}
                      onChange={(event) =>
                        setLines((current) =>
                          current.map((one, i) =>
                            i === index ? { ...one, quantity: Math.max(1, Number(event.target.value) || 1) } : one,
                          ),
                        )
                      }
                      className="min-h-11 w-20 rounded-btn border border-line bg-white px-2 text-center text-[14px] text-ink outline-none focus:border-blue"
                    />
                    <button
                      type="button"
                      onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                      className="min-h-11 bg-transparent px-1 text-[12.5px] font-medium text-danger"
                    >
                      Remove
                    </button>
                  </div>

                  {/* Flagged on the line, not buried in the note: it is the one
                      thing a buyer most needs to notice before accepting. */}
                  <label className="mt-2 flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={line.is_substitute ?? false}
                      onChange={(event) =>
                        setLines((current) =>
                          current.map((one, i) =>
                            i === index ? { ...one, is_substitute: event.target.checked } : one,
                          ),
                        )
                      }
                      className="mt-0.5"
                    />
                    <span className="text-[12.5px] leading-snug text-ink">
                      This is a substitute for something else on their prescription
                    </span>
                  </label>

                  {line.is_substitute ? (
                    <input
                      value={line.substitute_for ?? ''}
                      onChange={(event) =>
                        setLines((current) =>
                          current.map((one, i) =>
                            i === index ? { ...one, substitute_for: event.target.value } : one,
                          ),
                        )
                      }
                      placeholder="What it replaces, as written on the paper"
                      className="mt-2 min-h-11 w-full rounded-btn border border-caution bg-white px-3 text-[13.5px] text-ink outline-none focus:border-blue"
                    />
                  ) : null}
                </Card>
              );
            })}
          </div>

          <p className="m-0 mt-3 text-right font-display text-[19px] font-bold tracking-[-0.02em] text-blue tabular">
            {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 }).format(total / 100)}
          </p>
        </section>
      ) : null}

      <label className="mt-4 block">
        <span className="mb-1.5 block text-[12.5px] font-medium text-ink">
          Anything they should know
        </span>
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={3}
          placeholder="We have all of it in stock today. The 500mg is a different brand, same strength."
          className="w-full rounded-btn border border-line bg-white p-2.5 text-[14px] text-ink outline-none focus:border-blue"
        />
      </label>

      {error ? (
        <p role="alert" className="m-0 mt-3 text-[13px] font-medium leading-relaxed text-danger">
          {error.message}
        </p>
      ) : null}

      <Button
        size="lg"
        className="mt-4 w-full"
        disabled={lines.length === 0 || send.isPending}
        onClick={() => send.mutate()}
      >
        {send.isPending ? 'Sending…' : 'Send this price'}
      </Button>

      <p className="m-0 mt-2 text-[12px] leading-relaxed text-muted">
        Prices come from your own listings, so you are never quoting something you would not sell at.
        If they accept, it becomes an ordinary order and settles the way every other one does.
      </p>
    </div>
  );
}
